import clsx from "clsx";

import type { ProjectSchema, DepartmentSchema } from "@nexttrack/api-schema";

type Props = {
    projects: ProjectSchema[];
    departments: DepartmentSchema[];
    projectId?: string;
    departmentId?: string;
};

function coverageColour(pct: number): string {
    if (pct > 110) return "text-red-600";
    if (pct < 80) return "text-yellow-600";
    return "text-slate-600";
}

function barColour(pct: number): string {
    if (pct > 110) return "bg-red-500";
    if (pct < 80) return "bg-yellow-400";
    return "bg-blue-500";
}

function getDeptName(departments: DepartmentSchema[], id: string): string {
    return departments.find((d) => d.id === id)?.name ?? id;
}

export const BudgetCoveragePanel = ({ projects, departments, projectId, departmentId }: Props) => {
    const visibleProjects = projects.filter((p) => {
        if (p.archived) return false;
        if (projectId && p.id !== projectId) return false;
        return p.budgets.length > 0;
    });

    if (visibleProjects.length === 0) {
        return <p className="px-4 py-6 text-sm text-slate-400">No active budgets to display.</p>;
    }

    return (
        <div className="divide-y">
            {visibleProjects.map((project) => (
                <div key={project.id} className="px-4 py-3 space-y-3">
                    <p className="text-sm font-semibold text-slate-700">{project.name}</p>
                    {project.budgets.map((budget) => {
                        const visibleServices = budget.services.filter(
                            (s) => !departmentId || s.departmentId === departmentId,
                        );
                        if (visibleServices.length === 0) return null;
                        return (
                            <div key={budget.id} className="space-y-2">
                                <p className="text-xs font-medium text-slate-500">
                                    {budget.name}{" "}
                                    <span className="font-normal">
                                        ({budget.startDate} – {budget.endDate})
                                    </span>
                                </p>
                                {visibleServices.map((service) => {
                                    const committed = service.committedManDays;
                                    const booked = service.bookedManDays;
                                    const pct = committed > 0 ? Math.round((booked / committed) * 100) : 100;
                                    const fillPct = Math.min(100, (booked / Math.max(committed, 0.01)) * 100);
                                    return (
                                        <div key={service.id} className="space-y-0.5">
                                            <div className="flex items-center justify-between text-xs">
                                                <span className="text-slate-600 truncate mr-1">
                                                    {getDeptName(departments, service.departmentId)}
                                                </span>
                                                <span
                                                    className={clsx(
                                                        "font-semibold whitespace-nowrap",
                                                        coverageColour(pct),
                                                    )}
                                                >
                                                    {pct}%
                                                </span>
                                            </div>
                                            <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                                                <div
                                                    className={clsx(
                                                        "h-1.5 rounded-full transition-all",
                                                        barColour(pct),
                                                    )}
                                                    style={{ width: `${fillPct}%` }}
                                                />
                                            </div>
                                            <p className="text-xs text-slate-400">
                                                {booked.toFixed(1)}/{committed.toFixed(1)} MD
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};
